import React from 'react';
import styled, { css } from 'styled-components/macro';
import { JoinButton } from 'src/client/components/buttons/JoinButton';
import {
  DESKTOP,
  DESKTOP_WIDE,
  MOBILE_LANDSCAPE,
  TABLET_LANDSCAPE,
  TABLET_PORTRAIT,
} from 'src/client/constants/mediaQueries';
import { useSelector } from 'src/client/redux';
import { signalAnalyticsData } from 'src/client/utils/sendAnalyticsData';
import { CategoryIds, MeasurementTypes, ConversionTypes } from 'src/shared/constants/userMeasurement';
import { getDeviceType } from 'src/client/utils/getDeviceType';
import { useCookies } from 'react-cookie';
import { ANALYTICS_COOKIE_NAME, HIDE_FREE_SITES_BUTTON } from 'src/shared/constants/cookies';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

type Props = {
  canShowMemberStartNow: boolean;
};

export const MembersPortalFloater = (props: Props) => {
  const { canShowMemberStartNow } = props;
  const [cookies, setCookie] = useCookies([ANALYTICS_COOKIE_NAME, HIDE_FREE_SITES_BUTTON]);
  const { user } = useSelector((state) => state.auth);

  const analyticsUUID: string = cookies[ANALYTICS_COOKIE_NAME];
  const hideFreeSites: string = cookies[HIDE_FREE_SITES_BUTTON];
  const joinOverrideSite = canShowMemberStartNow ? undefined : false;
  const joinCampaign = canShowMemberStartNow ? 'free-trial-floater' : 'gateway-replacement-floater';
  const dataVariant = canShowMemberStartNow ? 'start-now' : 'members-portal';
  const buttonText = canShowMemberStartNow ? 'START NOW!' : 'Free Sites';

  const onClose = () => {
    const expires = new Date(new Date().getTime() + 3600 * 1000 * 24); // 1 day
    setCookie(HIDE_FREE_SITES_BUTTON, true, { expires, path: '/' });
  };

  const handlePortalLinkClick = () => {
    if (canShowMemberStartNow) {
      return;
    }

    signalAnalyticsData(
      {
        categoryId: CategoryIds.MembersPortalFloaty,
        conversionType: ConversionTypes.Click,
        deviceType: getDeviceType(),
      },
      analyticsUUID,
      MeasurementTypes.Conversion,
    );
  };

  const showMembersPortalFloater = Boolean(!hideFreeSites && (canShowMemberStartNow || user?.showMembersPortal));

  if (!showMembersPortalFloater) {
    return null;
  }

  return (
    <LinkWrapper canShowMemberStartNow={canShowMemberStartNow} data-variant={dataVariant}>
      <CloseButton onClick={onClose}>
        <FontAwesomeIcon icon={faTimes} />
      </CloseButton>
      <PortalLink
        campaign={joinCampaign}
        overrideSite={joinOverrideSite}
        rel="noopener noreferrer"
        target="_blank"
        onClick={handlePortalLinkClick}
      >
        {buttonText}
      </PortalLink>
    </LinkWrapper>
  );
};

const LinkWrapper = styled.div<{ canShowMemberStartNow: boolean }>`
  position: fixed;
  top: 50%;
  right: 0;
  padding: 5px 20px;
  z-index: 1000;
  background-color: #ceab58;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  transform: rotate(-90deg) translateX(50%);
  transform-origin: right bottom;

  ${({ canShowMemberStartNow }) =>
    canShowMemberStartNow &&
    css`
      @media (orientation: landscape) {
        display: none;
      }

      @media ${TABLET_PORTRAIT} {
        display: none;
      }
    `};
`;

const CloseButton = styled.div`
  position: absolute;
  align-items: center;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  color: #000;
  cursor: pointer;
  display: flex;
  font-size: 15px;
  height: 20px;
  justify-content: center;
  right: -10px;
  top: -10px;
  width: 20px;
  z-index: 2000;

  @media ${MOBILE_LANDSCAPE} {
    display: none;
  }
`;

const PortalLink = styled(JoinButton)`
  position: relative;
  text-decoration: none;
  cursor: pointer;
  width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  color: black;
  font-family: 'Open Sans', sans-serif;
  font-size: min(25px, 6vw);
  line-height: 1.2;

  @media ${MOBILE_LANDSCAPE} {
    font-size: 3vw;
  }

  @media ${TABLET_PORTRAIT} {
    font-size: 2.3vw;
  }

  @media ${TABLET_LANDSCAPE} {
    font-size: 2vw;
  }

  @media ${DESKTOP} {
    font-size: 2vw;
  }

  @media ${DESKTOP_WIDE} {
    font-size: min(25px, 1.5vw);
  }
`;
